import { useState, useEffect, useCallback } from "react";
import dashboardService from "../services/DashboardService";
import { formatDate } from "../../../utilities/formatDate";

const useDashboardData = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    userInfo: null,
    walletInfo: null,
    extracoinInfo: null,
    extrabonusInfo: null,
    lifetimeBonusInfo: null,
    directsInfo: null,
    allNodesInfo: null,
    subscriptionInfo: null,
    activationInfo: null,
    extracoinPercentage: null,
    pendingStoreTransactionsCount: 0,
  });

  const fetchAllData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [
        userInfoRes,
        walletInfoRes,
        extracoinInfoRes,
        extrabonusInfoRes,
        lifetimeBonusInfoRes,
        directsInfoRes,
        allNodesInfoRes,
        subscriptionInfoRes,
        activationInfoRes,
        extracoinPercentageRes,
        storePaymentsRes,
      ] = await Promise.allSettled([
        dashboardService.getUserInfo(),
        dashboardService.getWalletInfo(),
        dashboardService.getExtracoinInfo(),
        dashboardService.getWalletExtrabonus(),
        dashboardService.getWalletLifetimeBonus(),
        dashboardService.getNetworkDirectsInfo(),
        dashboardService.getNetworkAllInfo(),
        dashboardService.getSubscriptionInfo(),
        dashboardService.getActivationInfo(),
        dashboardService.getExtracoinPercentage(),
        dashboardService.getStorePayments(),
      ]);

      const newData = {
        userInfo: null,
        walletInfo: null,
        extracoinInfo: null,
        extrabonusInfo: null,
        lifetimeBonusInfo: null,
        directsInfo: null,
        allNodesInfo: null,
        subscriptionInfo: null,
        activationInfo: null,
        extracoinPercentage: null,
        pendingStoreTransactionsCount: 0,
      };

      if (userInfoRes.status === "fulfilled" && userInfoRes.value.messageCode === 200001) {
        newData.userInfo = userInfoRes.value.result.user;
      }

      if (walletInfoRes.status === "fulfilled" && walletInfoRes.value.messageCode === 200001) {
        newData.walletInfo = walletInfoRes.value.result;
      }

      if (
        extracoinInfoRes.status === "fulfilled" &&
        extracoinInfoRes.value.messageCode === 200001
      ) {
        newData.extracoinInfo = extracoinInfoRes.value.result;
      }

      if (
        extrabonusInfoRes.status === "fulfilled" &&
        extrabonusInfoRes.value.messageCode === 200001
      ) {
        newData.extrabonusInfo = extrabonusInfoRes.value.result;
      }

      if (
        lifetimeBonusInfoRes.status === "fulfilled" &&
        lifetimeBonusInfoRes.value.messageCode === 200001
      ) {
        newData.lifetimeBonusInfo = lifetimeBonusInfoRes.value.result;
      }

      if (directsInfoRes.status === "fulfilled" && directsInfoRes.value.messageCode === 200001) {
        newData.directsInfo = directsInfoRes.value.result;
      }

      if (allNodesInfoRes.status === "fulfilled" && allNodesInfoRes.value.messageCode === 200001) {
        newData.allNodesInfo = allNodesInfoRes.value.result;
      }

      if (
        subscriptionInfoRes.status === "fulfilled" &&
        subscriptionInfoRes.value.messageCode === 200001
      ) {
        const subscription = subscriptionInfoRes.value.result;
        if (subscription?.dateBegin && subscription?.dateEnd) {
          newData.subscriptionInfo = {
            ...subscription,
            subscriptionFormattedData: `${formatDate(new Date(subscription.dateBegin))} - ${formatDate(new Date(subscription.dateEnd))}`,
            subscriptionDaysLeft: Math.floor(
              (new Date(subscription.dateEnd) - Date.now()) / 86400000,
            ),
          };
        } else if (subscription) {
          newData.subscriptionInfo = subscription;
        }
      }

      if (
        activationInfoRes.status === "fulfilled" &&
        activationInfoRes.value.messageCode === 200001
      ) {
        const activation = activationInfoRes.value.result;
        if (activation?.activation?.dateBegin && activation?.activation?.dateEnd) {
          newData.activationInfo = {
            ...activation,
            activationFormattedData: `${formatDate(new Date(activation.activation.dateBegin))} - ${formatDate(new Date(activation.activation.dateEnd))}`,
            activationDaysLeft: Math.floor(
              (new Date(activation.activation.dateEnd) - Date.now()) / 86400000,
            ),
          };
        } else if (activation?.activation) {
          newData.activationInfo = activation;
        }
      }

      if (
        extracoinPercentageRes.status === "fulfilled" &&
        extracoinPercentageRes.value.messageCode === 200001
      ) {
        newData.extracoinPercentage = extracoinPercentageRes.value.result;
      }

      if (
        storePaymentsRes.status === "fulfilled" &&
        storePaymentsRes.value.messageCode === 200001
      ) {
        const transactions = storePaymentsRes.value.result || [];
        newData.pendingStoreTransactionsCount = transactions.filter(
          (t) => !t?.dateTimePayment
        ).length;
      }

      setData(newData);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const refreshData = useCallback(() => {
    fetchAllData();
  }, [fetchAllData]);

  return {
    data,
    isLoading,
    error,
    refreshData,
  };
};

export default useDashboardData;

